import React from "react";
import {
  Recycle,
  ShieldCheck,
  Boxes,
  Settings2,
  Leaf,
  Globe,
  MapPin,
  Phone,
  Mail,
  Clock,
} from "lucide-react";
import { servicesConfig, contactInfo } from "../mock";
import { WhatsAppIcon, waLink } from "./WhatsApp";
import { useLang } from "../i18n";
import { track } from "../analytics";

const serviceIconMap = {
  Recycle,
  ShieldCheck,
  Boxes,
  Settings2,
  Leaf,
  Globe,
};

export const ServicesSection = () => {
  const { t } = useLang();
  return (
    <section id="services" className="max-w-[1280px] mx-auto px-6 py-16 lg:py-24">
      <div className="text-center mb-12">
        <span className="text-green-600 font-bold uppercase tracking-widest text-sm">
          {t.services.eyebrow}
        </span>
        <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-slate-800">
          {t.services.title}
        </h2>
        <p className="mt-3 text-slate-500 max-w-2xl mx-auto">{t.services.sub}</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {servicesConfig.map((s) => {
          const Icon = serviceIconMap[s.icon];
          const item = t.services.items[s.id];
          return (
            <div
              key={s.id}
              className="group rounded-2xl border border-slate-200 bg-white p-7 hover:border-green-300 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-green-600/10 flex items-center justify-center group-hover:bg-green-600 transition-colors">
                {Icon && (
                  <Icon className="w-7 h-7 text-green-600 group-hover:text-white transition-colors" />
                )}
              </div>
              <h3 className="mt-5 font-bold text-xl text-slate-800">{item.title}</h3>
              <p className="mt-2 text-slate-500 leading-relaxed">{item.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ContactSection = () => {
  const { t } = useLang();
  const c = t.contact;
  const infoItems = [
    { icon: MapPin, label: c.labels.location, value: c.values.location },
    { icon: Phone, label: c.labels.phone, value: contactInfo.phone },
    { icon: Mail, label: c.labels.email, value: contactInfo.email },
    { icon: Clock, label: c.labels.hours, value: c.values.hours },
  ];

  return (
    <section id="contact" className="bg-slate-50 border-t border-slate-100">
      <div className="max-w-[1280px] mx-auto px-6 py-16 lg:py-24">
        <div className="text-center mb-12">
          <span className="text-green-600 font-bold uppercase tracking-widest text-sm">
            {c.eyebrow}
          </span>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-slate-800">
            {c.title}
          </h2>
          <p className="mt-3 text-slate-500 max-w-2xl mx-auto">{c.sub}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              {infoItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200"
                >
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-green-600/10 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 font-medium">{item.label}</div>
                    <div className="font-bold text-slate-800">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 h-[260px]">
              <iframe
                title="Tele-Comp Solutions location"
                src="https://www.google.com/maps?q=8243+NW+66th+St,+Miami,+Florida+33166&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* WhatsApp CTA panel */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm flex flex-col">
            <div className="w-16 h-16 rounded-2xl bg-[#25D366]/10 flex items-center justify-center">
              <WhatsAppIcon className="w-9 h-9 text-[#25D366]" />
            </div>
            <h3 className="mt-5 text-2xl font-extrabold text-slate-800">{c.waTitle}</h3>
            <p className="mt-2 text-slate-500 leading-relaxed">{c.waText}</p>

            <div className="mt-6 space-y-3">
              {c.quickMessages.map((msg) => (
                <a
                  key={msg}
                  href={waLink(msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { label: "quick" })}
                  className="flex items-center justify-between gap-3 px-5 py-4 rounded-xl border border-slate-200 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all text-slate-700 font-medium"
                >
                  <span>{msg}</span>
                  <WhatsAppIcon className="w-5 h-5 text-[#25D366] shrink-0" />
                </a>
              ))}
            </div>

            <a
              href={waLink(t.whatsapp.defaultMessage)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { label: "start" })}
              className="mt-6 inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold transition-colors"
            >
              <WhatsAppIcon className="w-6 h-6" /> {c.startChat}
            </a>

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-slate-200 hover:border-green-600 text-slate-800 font-bold transition-colors"
              >
                <Phone className="w-5 h-5 text-green-600" /> {c.callUs}
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-slate-200 hover:border-green-600 text-slate-800 font-bold transition-colors"
              >
                <Mail className="w-5 h-5 text-green-600" /> {c.emailUs}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
